import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { IChangeArgs, StackPanel } from '@agilepoint/wat-react-ux';
import { ITranslateFunction, useTranslate } from '@agilepoint/wat-react';
import { CustomTooltip, ElasticTextBoxField, FileUploadField, HorizontalLine } from 'common';
import { PlatformTypes } from '../steps';
import { BuildDetailsFields, RelativePositionedFlexbox } from '../styled-components';

const stopPropagation = (ev: React.MouseEvent<HTMLElement>) => ev.stopPropagation();

//NOTE: Important key, value, DO NOT CHANGE!!!
export const getBuildOptions = (translate: ITranslateFunction) => [{
    name: translate('modules.createNewApp.steps.platform.mdmBuildText'),
    title: translate('modules.createNewApp.steps.platform.mdmBuildText'),
    value: 'MDM',
    key: 'MDM'
}, {
    name: translate('modules.createNewApp.steps.platform.storeBuildText'),
    title: translate('modules.createNewApp.steps.platform.storeBuildText'),
    value: 'Store',
    key: 'Store'
}, {
    name: translate('modules.createNewApp.steps.platform.deviceSpecificBuildText'),
    title: translate('modules.createNewApp.steps.platform.deviceSpecificBuildText'),
    value: 'Device',
    key: 'Device'
}];

export const AndroidBuildDetailsForm = () => {
    const { watch } = useFormContext();
    const translate = useTranslate();
    const options = React.useMemo(() => getBuildOptions(translate), [translate]);

    const buildTypesValue = watch(`${PlatformTypes.Android}.buildTypes`);
    const validateFile = useCallback((value: IChangeArgs) => {
        if (!value.selectedFiles[0]?.name.endsWith('.key')) {
            return translate('modules.createNewApp.steps.platform.uploadKeyFileText');
        }
        if (!value.selectedFiles.length) {
            return translate('modules.createNewApp.steps.platform.pleaseUploadFileText');
        }
        return true;
    }, [translate]);

    const validatePackageName = useCallback((value: string) => {
        const regex = /^[a-zA-Z0-9._-]+$/;
        if(!regex.test(value)) {
            return 'Only hypen, period, underscore and alpha numberic characters are allowed.';
        }
        return true;
    }, []);

    return (
        <>
            <HorizontalLine $margin='17px 0px 25px 0px !important' />
            <StackPanel onClick={stopPropagation} orientation="vertical" gap="20" width="100%">
                <BuildDetailsFields
                    groupName="android-build-details"
                    name={`${PlatformTypes.Android}.buildTypes`}
                    options={options}
                    orientation="vertical"/>
                <RelativePositionedFlexbox width='310px'>
                    <ElasticTextBoxField
                        name={`${PlatformTypes.Android}.packageName`}
                        label={translate('modules.createNewApp.steps.platform.packageNameLabel')}
                        title={translate('modules.createNewApp.steps.platform.packageNameLabel')}
                        validationRules={{
                            required: translate('modules.createNewApp.steps.platform.requiredPackageNameText'),
                            validate: validatePackageName
                        }} />
                    <CustomTooltip
                        tooltipContent={translate('modules.createNewApp.steps.platform.packageNameHelpIconText')} />
                </RelativePositionedFlexbox>
                {
                    buildTypesValue === 'Store' ?
                        <>
                            <FileUploadField
                                name={`${PlatformTypes.Android}.certificate`}
                                title={translate('modules.createNewApp.steps.platform.certificateLabel')}
                                label={translate('modules.createNewApp.steps.platform.certificateLabel')}
                                orientation='vertical'
                                validationRules={{ required: 'required', validate: validateFile }}
                                accept=".key"
                                readMode="readAsDataURL" />
                            <ElasticTextBoxField
                                name={`${PlatformTypes.Android}.keyAlias`}
                                title={translate('modules.createNewApp.steps.platform.keyAliasLabel')}
                                label={translate('modules.createNewApp.steps.platform.keyAliasLabel')}
                                validationRules={{
                                    required: translate('modules.createNewApp.steps.platform.keyAliasRequiredText')
                                }} />
                            <ElasticTextBoxField
                                name={`${PlatformTypes.Android}.keyPassword`}
                                title={translate('modules.createNewApp.steps.platform.keyPasswordLabel')}
                                label={translate('modules.createNewApp.steps.platform.keyPasswordLabel')}
                                type="password"
                                validationRules={{
                                    required: translate('modules.createNewApp.steps.platform.keyPasswordRequiredText')
                                }} />
                            <ElasticTextBoxField
                                name={`${PlatformTypes.Android}.storePassword`}
                                title={translate('modules.createNewApp.steps.platform.storePasswordLabel')}
                                label={translate('modules.createNewApp.steps.platform.storePasswordLabel')}
                                type="password"
                                validationRules={{
                                    required: translate('modules.createNewApp.steps.platform.storePasswordRequiredText')
                                }} />
                        </>
                        : <></>
                }
            </StackPanel>
        </>
    );
};
